import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Target,
  Users,
  Trophy,
  Star,
  ArrowRight,
  CheckCircle,
  Heart,
  Shield,
  Globe,
  Award,
  TrendingUp,
  Zap,
  Phone,
  Mail,
  MapPin,
  Linkedin,
  Twitter
} from "lucide-react";
import tommyCanaleImg from "@/assets/tommy-canale.png";

export default function About() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/lovable-uploads/f412fccf-e846-40ef-b721-4ebb334cd341.png"
            alt="RecruitLook Hoops event with coaches and athletes in gymnasium"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary text-black px-6 py-3 text-sm font-black tracking-wider mb-8">
              ESTABLISHED 2008
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
              ABOUT
              <span className="block text-secondary">RECRUITLOOK</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Dedicated to assisting student-athletes in gaining college recruiting exposure through innovative
              recruiting technologies, saving families millions in scholarship funds since 2008.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-black text-secondary mb-2">16+</div>
              <div className="text-lg font-semibold">Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-black text-secondary mb-2">Millions</div>
              <div className="text-lg font-semibold">Saved in Scholarships</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-black text-secondary mb-2">Multi-Sport</div>
              <div className="text-lg font-semibold">Coverage</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-black text-secondary mb-2">Nationwide</div>
              <div className="text-lg font-semibold">Events & Exposure</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black mb-8 text-slate-900">
                OUR
                <span className="block text-secondary">STORY</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in 2008 by Mike Enright, RecruitLook began with a simple mission: to provide athletes and families with the resources and guidance needed to navigate the competitive college recruiting process. Mike's career as a traveling scout, where he worked directly with college coaches to identify standout prospects, laid the foundation for RecruitLook's commitment to transparency, integrity, and personalized service.
                </p>
                <p>
                  Over the years, RecruitLook has evolved into a leader in athlete recruitment, introducing cutting-edge technologies and expanding its services to include a diverse range of grassroots sports events—from basketball tournaments and football combines to baseball showcases, junior golf tours, and comprehensive college recruiting seminars.
                </p>
                <p>
                  Our holistic approach is designed to give each athlete the visibility and support they need to succeed. With a focus on long-term partnerships and individualized strategies, we've helped families save millions in scholarship funds, transforming dreams into reality and providing the guidance that empowers athletes to take their talents to the next level.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="/lovable-uploads/d605483c-319f-4a3b-bf02-663dc2c9b137.png"
                alt="Mike Enright and coaching staff discussing recruiting strategy"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-black p-6 rounded-xl">
                <div className="text-2xl font-black">16+ YEARS</div>
                <div className="text-sm font-bold">OF EXCELLENCE</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              OUR MISSION &
              <span className="block text-secondary">VALUES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything we do is guided by our core mission and unwavering values
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mission Card */}
            <div className="md:col-span-2 lg:col-span-3 bg-slate-900 text-white rounded-2xl p-8 lg:p-12 text-center">
              <Target className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-3xl lg:text-4xl font-black mb-6">OUR MISSION</h3>
              <p className="text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
                To scout, evaluate, educate, and guide student-athletes through the college recruiting process.
                We emphasize a personalized approach, avoiding generic outreach to college coaches and instead
                focusing on tailored support for each athlete and their family.
              </p>
            </div>

            {/* Values Cards */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Heart className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">INTEGRITY</h4>
              <p className="text-gray-600">
                We operate with complete transparency, honesty, and ethical standards in every interaction 
                with athletes, families, and college programs.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Trophy className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">EXCELLENCE</h4>
              <p className="text-gray-600">
                We strive for excellence in everything we do, from our technology platform to our personal 
                service, ensuring the best possible outcomes for our athletes.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Users className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">COMMUNITY</h4>
              <p className="text-gray-600">
                We build lasting relationships and foster a supportive community where athletes, families, 
                and coaches can thrive together.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Zap className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">INNOVATION</h4>
              <p className="text-gray-600">
                We continuously innovate and improve our platform, using cutting-edge technology to enhance 
                the recruiting experience for all stakeholders.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Shield className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">ACCOUNTABILITY</h4>
              <p className="text-gray-600">
                We take responsibility for our commitments and hold ourselves to the highest standards 
                of service and results for our athletic community.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <Globe className="w-12 h-12 text-secondary mb-4" />
              <h4 className="text-2xl font-black mb-4 text-slate-900">ACCESSIBILITY</h4>
              <p className="text-gray-600">
                We believe in making college recruiting accessible to all, breaking down barriers and 
                creating opportunities regardless of economic or geographic circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              LEADERSHIP
              <span className="block text-secondary">TEAM</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced leaders driving our mission forward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mike Enright - Founder */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 group hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-[32rem] overflow-hidden">
                <img
                  src="/lovable-uploads/6dafcaa8-d351-4d75-b3df-504ab7d567a6.png"
                  alt="Mike Enright"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-1">MIKE ENRIGHT</h3>
                <p className="text-secondary font-bold text-sm mb-3">Founder</p>
                <p className="text-gray-600 text-sm mb-4">
                  Began his career as a traveling scout, creating RecruitLook in 2008. His passion for helping families has led to saving millions in scholarships and building some of the biggest recruiting events around the country.
                </p>
              </div>
            </div>

            {/* Tommy Canale */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 group hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-[32rem] overflow-hidden">
                <img
                  src={tommyCanaleImg}
                  alt="Tommy Canale"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-1">TOMMY CANALE</h3>
                <p className="text-secondary font-bold text-sm mb-3">National Scout & Director</p>
                <p className="text-gray-600 text-sm mb-4">
                  Former Athletic Director and basketball coach at Illinois Valley Community College. Expert in talent identification and development.
                </p>
              </div>
            </div>

            {/* Larry Hughes */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 group hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-[32rem] overflow-hidden">
                <img
                  src="/lovable-uploads/21c25279-d7cd-45b7-9102-f0c327610120.png"
                  alt="Larry Hughes"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-1">LARRY HUGHES</h3>
                <p className="text-secondary font-bold text-sm mb-3">Camp Director & Brand Ambassador</p>
                <p className="text-gray-600 text-sm mb-4">
                  Lead instructor for our Elite Camp, Larry is a former NBA player drafted 1st round 1998 by Philadelphia 76ers. Played for multiple NBA teams including Warriors and Wizards.
                </p>
              </div>
            </div>

            {/* Dana Morgan */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 group hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-[32rem] overflow-hidden">
                <img
                  src="/lovable-uploads/e5a002f6-2679-4cb4-8457-b92acb057b71.png"
                  alt="Dana Morgan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-1">DANA MORGAN</h3>
                <p className="text-secondary font-bold text-sm mb-3">Team Sales & Event Operations</p>
                <p className="text-gray-600 text-sm mb-4">
                  Known for building strong relationships with coaches and partners while playing a key role in organizing and executing our sporting events.
                </p>
              </div>
            </div>

            {/* Jarrell Strong */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 group hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-[32rem] overflow-hidden">
                <img
                  src="/lovable-uploads/93d89140-0675-4b26-8f1a-56cffdc4a6f3.png"
                  alt="Jarrell Strong"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-1">JARRELL STRONG</h3>
                <p className="text-secondary font-bold text-sm mb-3">Marketing & Event Operations</p>
                <p className="text-gray-600 text-sm mb-4">
                  Passionate about innovation and storytelling, Strong plays a key role in shaping the company's market presence and long-term vision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6">
              WHY CHOOSE
              <span className="block text-secondary">RECRUITLOOK?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Athletes are seeing an increase in their college recruitment compared to the national average.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <TrendingUp className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">PROVEN RESULTS</h3>
              <p className="text-gray-300">
                94% placement rate with $450M+ in scholarships secured for our athletes.
              </p>
            </div>

            <div className="text-center">
              <Users className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">PERSONAL APPROACH</h3>
              <p className="text-gray-300">
                Dedicated recruiting specialists work one-on-one with each athlete and family.
              </p>
            </div>

            <div className="text-center">
              <Globe className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">NATIONWIDE NETWORK</h3>
              <p className="text-gray-300">
                Direct relationships with 1,200+ college programs across all divisions and sports.
              </p>
            </div>

            <div className="text-center">
              <Shield className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">COMPLIANCE FIRST</h3>
              <p className="text-gray-300">
                Expert knowledge of NCAA regulations ensures all activities remain compliant.
              </p>
            </div>

            <div className="text-center">
              <Zap className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">CUTTING-EDGE TECH</h3>
              <p className="text-gray-300">
                Advanced matching algorithms and data analytics optimize your recruiting process.
              </p>
            </div>

            <div className="text-center">
              <Award className="w-16 h-16 text-secondary mx-auto mb-6" />
              <h3 className="text-2xl font-black mb-4">INDUSTRY RECOGNITION</h3>
              <p className="text-gray-300">
                Award-winning platform recognized by coaches, athletes, and industry experts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-black mb-8 text-slate-900">
            READY TO START
            <span className="block text-secondary">YOUR JOURNEY?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join thousands of student-athletes who have transformed their college dreams into reality with RecruitLook.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-black text-xl px-12 py-6 font-black tracking-wide">
              GET STARTED TODAY
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
